import { useEffect, useRef, useState } from "react";
import { GRADES, ZONES, DOMAINS, CATEGORIES, COUNTY_GROUPS, NON_COUNTY_MARKETS } from "@/utils/constants";
import { GradeExplainer } from "@/components/GradeExplainer";
import { NearbyZipFilter } from "@/components/directory/NearbyZipFilter";

const countyLabel = (county) => (NON_COUNTY_MARKETS.has(county) ? county : `${county} County`);

function FilterChip({ label, active, onClick, testId }) {
  return (
    <button onClick={onClick} className={`filter-chip ${active ? "filter-chip-active" : "filter-chip-idle"}`} data-testid={testId}>
      {label}
    </button>
  );
}

function getFilteredZones(query, activeCounty) {
  if (query) return ZONES.filter((zone) => zone.toLowerCase().includes(query.toLowerCase()));
  return activeCounty ? (COUNTY_GROUPS[activeCounty] || []) : ZONES.slice(0, 24);
}

function ZoneOptions({ zones, activeZone, onSelect }) {
  if (zones.length === 0) return <p className="px-3 py-4 text-sm font-body text-gray-500">No matching areas.</p>;
  return zones.map((zone) => (
      <button key={zone} onClick={() => onSelect(zone)} className={`w-full text-left px-3 py-2 text-sm font-body ${activeZone === zone ? "bg-navy text-white" : "text-gray-700 hover:bg-slate-50"}`} data-testid={`zone-option-${zone.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{zone}</button>
  ));
}

function ZoneSearch({ activeZone, onZoneChange, activeCounty, onCountyChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const close = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const filteredZones = getFilteredZones(query, activeCounty);
  const current = activeZone || (activeCounty ? countyLabel(activeCounty) : "All areas");
  const selectZone = (zone) => { onZoneChange(zone); onCountyChange(null); setQuery(""); setOpen(false); };

  return (
    <div ref={ref} className="relative min-w-0 md:w-64">
      <button onClick={() => setOpen((value) => !value)} className={`filter-control w-full text-left ${activeZone || activeCounty ? "filter-control-active" : ""}`} data-testid="zone-filter-trigger" aria-expanded={open}>
        <span className="text-[10px] uppercase tracking-widest text-gray-500 block mb-0.5">Location</span>
        <span className="block truncate">{current}</span>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-2 w-[min(22rem,calc(100vw-2rem))] bg-white border-2 border-navy/15 shadow-[8px_8px_0_rgba(17,41,107,0.12)] z-[70] overflow-hidden" data-testid="zone-dropdown">
          <div className="p-3 border-b border-slate-200">
            <label className="block text-xs font-body font-semibold text-navy mb-1" htmlFor="zone-search">Search city or town</label>
            <input id="zone-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Start typing a place" className="w-full border border-slate-300 px-3 py-2 font-body text-sm text-navy focus:outline-none focus:border-navy" autoFocus data-testid="zone-search-input" />
          </div>
          {!query && (
            <div className="p-3 border-b border-slate-100">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-body mb-2">Browse counties</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.keys(COUNTY_GROUPS).map((county) => (
                  <button key={county} onClick={() => { onCountyChange(activeCounty === county ? null : county); onZoneChange(null); }} className={`text-xs font-body px-2.5 py-1.5 border ${activeCounty === county ? "bg-navy text-white border-navy" : "bg-white text-gray-600 border-slate-200 hover:border-navy"}`} data-testid={`county-chip-${county.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{county}</button>
                ))}
              </div>
            </div>
          )}
          <div className="max-h-56 overflow-y-auto p-2">
            {(activeZone || activeCounty) && (
              <button onClick={() => { onZoneChange(null); onCountyChange(null); setQuery(""); setOpen(false); }} className="w-full text-left px-3 py-2 text-sm font-body font-semibold text-brand-red hover:bg-slate-50" data-testid="zone-filter-clear">Clear location</button>
            )}
            <ZoneOptions zones={filteredZones} activeZone={activeZone} onSelect={selectZone} />
          </div>
        </div>
      )}
    </div>
  );
}

function SecondaryControls({ activeDomain, activeGrade, onToggleMore, panel, setPanel, showSecondary }) {
  const hasActive = Boolean(activeGrade || activeDomain);
  return <>
    <div className={`${showSecondary ? "grid" : "hidden"} col-span-2 grid-cols-2 gap-2 md:contents`} data-testid="filter-secondary-controls">
      <button onClick={() => setPanel(panel === "grade" ? null : "grade")} className={`filter-control min-w-0 text-left md:min-w-[150px] ${activeGrade ? "filter-control-active" : ""}`} data-testid="filter-grade-menu-toggle" aria-expanded={panel === "grade"}>
        <span className="text-[10px] uppercase tracking-widest text-gray-500 block mb-0.5">Community grade</span>
        <span>{activeGrade || "Any grade"}</span>
      </button>
      <button onClick={() => setPanel(panel === "domain" ? null : "domain")} className={`filter-control min-w-0 text-left md:min-w-[170px] ${activeDomain ? "filter-control-active" : ""}`} data-testid="filter-domain-menu-toggle" aria-expanded={panel === "domain"}>
        <span className="text-[10px] uppercase tracking-widest text-gray-500 block mb-0.5">Health focus</span>
        <span className="block truncate">{activeDomain || "Any focus"}</span>
      </button>
    </div>
    {!hasActive && <button type="button" onClick={onToggleMore} className="col-span-2 py-1 text-left font-mono text-[10px] font-bold uppercase text-navy underline underline-offset-4 md:hidden" aria-expanded={showSecondary} data-testid="filter-more-toggle">{showSecondary ? "Fewer filters" : "More filters · grade & health focus"}</button>}
  </>;
}

export default function FilterBar({
  activeCategory, onCategoryChange,
  activeGrade, onGradeChange,
  activeZone, onZoneChange,
  activeDomain, onDomainChange,
  categoryCounts = {}, activeCounty, onCountyChange,
  activeNearby, onNearbyApply, onNearbyClear,
}) {
  const [panel, setPanel] = useState(null);
  const [mobileMore, setMobileMore] = useState(false);
  const showSecondary = mobileMore || Boolean(activeGrade || activeDomain) || panel !== null;
  return (
    <div className="iw-grid-bg bg-white border-b border-navy/15" data-testid="directory-filters">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 md:py-4 space-y-2.5 md:space-y-4">
        <div>
          <p className="filter-group-label hidden md:block">Browse by type</p>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {CATEGORIES.map((category) => {
              const count = category.key === "all" ? Object.values(categoryCounts).reduce((sum, value) => sum + value, 0) : (categoryCounts[category.key] || 0);
              return <FilterChip key={category.key} label={`${category.label}${count ? ` · ${count.toLocaleString()}` : ""}`} active={activeCategory === category.key} onClick={() => onCategoryChange(category.key)} testId={`category-tab-${category.key}`} />;
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 md:flex md:flex-wrap md:items-stretch" data-testid="filter-controls-row">
          <ZoneSearch activeZone={activeZone} onZoneChange={onZoneChange} activeCounty={activeCounty} onCountyChange={onCountyChange} />
          {onNearbyApply && <NearbyZipFilter activeNearby={activeNearby} onApply={onNearbyApply} onClear={onNearbyClear} />}
          <SecondaryControls activeDomain={activeDomain} activeGrade={activeGrade} panel={panel} setPanel={setPanel} showSecondary={showSecondary} onToggleMore={() => setMobileMore((value) => !value)} />
        </div>

        {panel === "grade" && (
          <div className="border-t border-slate-200 pt-4" data-testid="grade-filter-panel">
            <div className="flex items-center justify-between gap-3 mb-2"><p className="filter-group-label mb-0">Choose a grade</p><GradeExplainer /></div>
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <FilterChip label="Any grade" active={!activeGrade} onClick={() => onGradeChange(null)} testId="filter-grade-all" />
              {GRADES.map((grade) => <FilterChip key={grade} label={grade} active={activeGrade === grade} onClick={() => onGradeChange(activeGrade === grade ? null : grade)} testId={`filter-grade-${grade}`} />)}
            </div>
          </div>
        )}

        {panel === "domain" && (
          <div className="border-t border-slate-200 pt-4" data-testid="domain-filter-panel">
            <p className="filter-group-label">Choose a health focus</p>
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <FilterChip label="Any focus" active={!activeDomain} onClick={() => onDomainChange(null)} testId="filter-domain-all" />
              {DOMAINS.map((domain) => <FilterChip key={domain} label={domain} active={activeDomain === domain} onClick={() => onDomainChange(activeDomain === domain ? null : domain)} testId={`filter-domain-${domain.replace(/ /g, "-")}`} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}